import React, { useCallback, useEffect, useMemo, useState } from "react";
import type { RowSelectionState } from "@tanstack/react-table";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useShadcnInsetChrome } from "@/contexts/ShadcnInsetChromeContext";
import { useShadcnInsetHeaderActions } from "@/contexts/ShadcnInsetHeaderActionsContext";
import { useShadcnInsetDynamicPageTitle } from "@/hooks/useShadcnInsetBreadcrumbTrail";
import { Button as UiButton } from "@/components/ui/button";
import {
  WORKSPACE_HUB_BREADCRUMB,
  WORKSPACE_HUB_PATH,
} from "../Workspace/workspaceRoutes";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Archive,
  Code as CodeIcon,
  FileCode,
  HelpCircle,
  Pencil,
  RotateCcw,
  Trash2,
} from "lucide-react";

import { StrategyDescriptionPanel } from "./StrategyDescriptionPanel";
import { EditStrategyDialog } from "./EditStrategyDialog";
import { presentMutationError, toastError, toastWarning } from "../../../utils/showApiErrorToast";
import {
  buildStrategyDetailMetaItems,
  StrategyCardMetaRowWithBadges,
  strategyDetailHeaderCardProps,
} from "./strategyDetailCardChrome";
import { strategiesMainCardClassName } from "./strategiesCardShell";
import { reportDetailSectionCardProps } from "../Reports/components/ReportsCardChrome";
import { WorkspaceDrawerActionRow } from "../Workspace/WorkspaceDrawerActionRow";
import {
  WorkspaceDrawerActionsMenu,
  type WorkspaceDrawerActionItem,
} from "../Workspace/WorkspaceDrawerActionsMenu";
import { StrategyPolicyVersionsTable } from "./StrategyPolicyVersionsTable";
import { usePolicyVersionBacktestReports } from "./usePolicyVersionBacktestReports";
import {
  DeletePolicyVersionsDialog,
  type DeletePolicyVersionsDialogRequest,
} from "./DeletePolicyVersionsDialog";
import { StrategyPolicyViewDialog } from "./StrategyPolicyViewDialog";
import { StrategyPineScriptViewDialog } from "./StrategyPineScriptViewDialog";
import {
  StatisticsEmptyState,
  statisticsPageMinHeight,
} from "../Statistics/components/statisticsEmptyState";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Code,
  Flex,
  Heading,
  HStack,
  IconButton,
  Spinner,
  Text,
  useColorModeValue,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { toast } from "sonner";
import { useDisplayTimeZone } from "../../../contexts/DisplayTimezoneContext";
import Card from "../../../components/Card/Card";
import CardBody from "../../../components/Card/CardBody";
import CardHeader from "../../../components/Card/CardHeader";
import {
  getReportStrategyDetail,
  getReportStrategyPolicyVersionPineScript,
  listReportStrategyPolicyVersions,
  deleteReportStrategy,
  deleteReportStrategyPolicyVersion,
  updateReportStrategy,
  updateReportStrategyPolicyVersion,
} from "../../../services/reportsApi";
import { getBacktest } from "../../../services/backtestsApi";
import { simulatorResultsWithBacktestPath } from "../ExecutionRulesSimulator/simulatorRoutes";
import type { ReportStrategyPolicyVersion, ReportStrategyResponse } from "../../../types/reports";
import type { PineParseSessionState } from "../../../components/Strategies/StrategyPineEditor";

import {
  formatStrategyTimestamp,
  getActiveComposedPolicyVersionIds,
  policyVersionDisplayTitle,
  VERSION_LABELS_STORAGE_KEY,
  versionLabel,
  type StrategyDetailLocationState,
} from "./strategyDetailUtils";
import { StrategyActiveTradingViewPine } from "../../../components/Strategies/StrategyActiveTradingViewPine";
import { StrategyComposeModal } from "../../../components/Strategies/StrategyComposeModal";
import { StrategyCombinePoliciesDialog } from "../../../components/Strategies/StrategyCombinePoliciesDialog";
import {
  dispatchWorkspacePineActiveShow,
  dispatchWorkspacePineComposeFailed,
  dispatchWorkspacePineComposeRefresh,
  dispatchWorkspacePineComposeStart,
  dispatchWorkspacePineMasterShow,
  WORKSPACE_PINE_COMPOSE_REFRESH,
  WORKSPACE_PINE_DOCK_VISIBILITY,
  type WorkspacePineDockVisibilityDetail,
} from "../Workspace/workspacePineComposeEvents";
import { StrategySetActivePineDialog } from "../../../components/Strategies/StrategySetActivePineDialog";
import { StrategyMasterPineSummary } from "../../../components/Strategies/StrategyMasterPineSummary";
import {
  filterPolicyVersionsForCompose,
  strategyHasMasterPinescriptUploaded,
  strategyHasTradingViewPinescriptCreated,
} from "../../../utils/reportStrategyPineUi";

type RouteParams = { strategyId: string };

export type StrategyDetailProps = {
  layout?: "page" | "embedded";
  strategyId?: string;
  onDeleted?: () => void;
  /** When embedded in the workspace drawer, closes the panel. */
  onClose?: () => void;
};

export default function StrategyDetail({
  layout = "page",
  strategyId: strategyIdProp,
  onDeleted,
  onClose,
}: StrategyDetailProps = {}): JSX.Element {
  const displayTimeZone = useDisplayTimeZone();
  const history = useHistory();
  const location = useLocation<StrategyDetailLocationState>();
  const { strategyId: strategyIdParam } = useParams<RouteParams>();
  const embedded = layout === "embedded";
  const strategyId = (strategyIdProp ?? strategyIdParam)?.trim() ?? "";
  const shadcnInset = useShadcnInsetChrome();
  const { setActions: setInsetHeaderActions } = useShadcnInsetHeaderActions();
  const pageMinHeight = embedded ? undefined : statisticsPageMinHeight(shadcnInset);

  const textColor = useColorModeValue("gray.700", "gray.200");
  const subtleText = useColorModeValue("gray.500", "gray.400");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  // Chakra responsive props can cause TS union inference blowups; render through `any` aliases.
  const BoxAny = Box as any;
  const FlexAny = Flex as any;

  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [selectedStrategy, setSelectedStrategy] = useState<ReportStrategyResponse | null>(null);
  const [currentPolicyVersionId, setCurrentPolicyVersionId] = useState<string | null>(null);

  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versionsError, setVersionsError] = useState<string | null>(null);
  const [versions, setVersions] = useState<ReportStrategyPolicyVersion[]>([]);
  const [preferredComposePolicyVersionId, setPreferredComposePolicyVersionId] = useState<string | null>(
    null
  );


  const { isOpen: editOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const [editPineSession, setEditPineSession] = useState<PineParseSessionState | null>(null);
  const [archivingStrategyId, setArchivingStrategyId] = useState<string | null>(null);
  const [restoringStrategyId, setRestoringStrategyId] = useState<string | null>(null);
  const [deletingStrategyId, setDeletingStrategyId] = useState<string | null>(null);

  const [versionLabels, setVersionLabels] = useState<Record<string, string>>(() => {
    try {
      const raw = window.localStorage.getItem(VERSION_LABELS_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) return parsed as Record<string, string>;
    } catch {
      // no-op
    }
    return {};
  });

  const [pineModalOpen, setPineModalOpen] = useState(false);
  const [pineLoading, setPineLoading] = useState(false);
  const [pineError, setPineError] = useState<string | null>(null);
  const [pineText, setPineText] = useState<string>("");
  const [pineVersionLabel, setPineVersionLabel] = useState<string>("");

  const [policyModalOpen, setPolicyModalOpen] = useState(false);
  const [policyModalTitle, setPolicyModalTitle] = useState<string>("");
  const [policyModalTree, setPolicyModalTree] = useState<unknown>(null);
  const [policyModalVersion, setPolicyModalVersion] =
    useState<ReportStrategyPolicyVersion | null>(null);

  const { isOpen: composeOpen, onOpen: onComposeOpen, onClose: onComposeClose } = useDisclosure();
  const {
    isOpen: combinePoliciesOpen,
    onOpen: onCombinePoliciesOpen,
    onClose: onCombinePoliciesClose,
  } = useDisclosure();
  const [activePineAutoOpenSignal, setActivePineAutoOpenSignal] = useState(0);
  const [activePineAutoOpenMode, setActivePineAutoOpenMode] = useState<"checklist" | "compose">(
    "checklist"
  );
  const [activePineComposeStartSignal, setActivePineComposeStartSignal] = useState(0);
  const [activePineComposeFailedSignal, setActivePineComposeFailedSignal] = useState(0);
  const [setActivePineTarget, setSetActivePineTarget] =
    useState<ReportStrategyPolicyVersion | null>(null);
  const [setActivePineBusy, setSetActivePineBusy] = useState(false);
  const [pendingComposeSwitch, setPendingComposeSwitch] = useState<{
    previousPreferredComposePolicyVersionId: string | null;
    previousActiveComposedPolicyVersionIds: string[];
  } | null>(null);
  const [policyVersionView, setPolicyVersionView] = useState<"active" | "archived">("active");
  const [activePolicyRowSelection, setActivePolicyRowSelection] = useState<RowSelectionState>({});
  const [archivedPolicyRowSelection, setArchivedPolicyRowSelection] = useState<RowSelectionState>({});
  const [policyBulkDeleteBusy, setPolicyBulkDeleteBusy] = useState(false);
  const [deletePolicyVersionsRequest, setDeletePolicyVersionsRequest] =
    useState<DeletePolicyVersionsDialogRequest | null>(null);
  const [detailTab, setDetailTab] = useState("overview");

  const navigateToPoliciesTab = useCallback(() => {
    setDetailTab("policies");
  }, []);

  const handleActivePineAutoOpenConsumed = useCallback(() => {
    setActivePineAutoOpenSignal(0);
  }, []);

  const handleActivePineComposeStarted = useCallback(() => {
    setDetailTab("overview");
  }, []);

  const notifyActivePineComposeStart = useCallback(() => {
    if (embedded && strategyId) {
      dispatchWorkspacePineComposeStart(strategyId);
      return;
    }
    setDetailTab("overview");
    setActivePineComposeStartSignal((value) => value + 1);
  }, [embedded, strategyId]);

  const notifyActivePineComposeFailed = useCallback(() => {
    if (embedded && strategyId) {
      dispatchWorkspacePineComposeFailed(strategyId);
      return;
    }
    setActivePineComposeFailedSignal((value) => value + 1);
  }, [embedded, strategyId]);

  const requestActivePineDialog = useCallback((mode: "checklist" | "compose" = "checklist") => {
    // Embedded workspace sheet uses the canvas pine dock; open the shared compose modal instead.
    if (embedded) {
      if (mode === "compose") onComposeOpen();
      return;
    }
    setActivePineAutoOpenMode(mode);
    window.requestAnimationFrame(() => {
      setActivePineAutoOpenSignal((v) => v + 1);
    });
  }, [embedded, onComposeOpen]);

  const fromWorkspace = location.state?.fromWorkspace === true;
  const strategyListExitPath = fromWorkspace ? WORKSPACE_HUB_PATH : "/strategies";
  const pendingStrategyTitle = useMemo(
    () => location.state?.strategyDisplayName?.trim() || null,
    [location.state?.strategyDisplayName]
  );
  const strategyBreadcrumbTitle = selectedStrategy?.name?.trim() || pendingStrategyTitle;
  useShadcnInsetDynamicPageTitle(embedded ? null : strategyBreadcrumbTitle, {
    parent: !embedded && fromWorkspace ? WORKSPACE_HUB_BREADCRUMB : null,
  });

  useEffect(() => {
    if (embedded) return;
    if (!strategyId) {
      history.replace(strategyListExitPath);
    }
  }, [embedded, history, strategyId, strategyListExitPath]);

  const loadDetail = useCallback(async (id: string) => {
    if (!id) return;
    setDetailLoading(true);
    setDetailError(null);
    try {
      const detail = await getReportStrategyDetail(id);
      setSelectedStrategy(detail);
      setCurrentPolicyVersionId(detail?.current_policy_version_id ?? null);
    } catch (e) {
      setSelectedStrategy(null);
      setCurrentPolicyVersionId(null);
      setDetailError(e instanceof Error ? e.message : "Failed to load strategy.");
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const loadVersions = useCallback(async (id: string) => {
    if (!id) return;
    setVersionsLoading(true);
    setVersionsError(null);
    try {
      const data = await listReportStrategyPolicyVersions(id, {
        include_archived: true,
      });
      // Backend may return either { versions: [...] } or a raw array.
      const list = Array.isArray((data as any)?.versions)
        ? (data as any).versions
        : Array.isArray(data)
          ? (data as any)
          : [];
      setVersions(list);
    } catch (e) {
      setVersions([]);
      setVersionsError(e instanceof Error ? e.message : "Failed to load versions.");
    } finally {
      setVersionsLoading(false);
    }
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(VERSION_LABELS_STORAGE_KEY, JSON.stringify(versionLabels));
    } catch {
      // no-op
    }
  }, [versionLabels]);



  useEffect(() => {
    if (!strategyId) {
      setSelectedStrategy(null);
      setVersions([]);
      setCurrentPolicyVersionId(null);
      return;
    }
    setPreferredComposePolicyVersionId(null);
    void loadDetail(strategyId);
    void loadVersions(strategyId);
  }, [loadDetail, loadVersions, strategyId]);


  const handleEditOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        setEditPineSession(null);
        onEditClose();
      } else {
        onEditOpen();
      }
    },
    [onEditClose, onEditOpen]
  );

  const refreshSelectedStrategy = useCallback(async () => {
    if (!strategyId) return;
    await loadDetail(strategyId);
    await loadVersions(strategyId);
  }, [loadDetail, loadVersions, strategyId]);

  const refreshSelectedStrategyAndWorkspacePine = useCallback(async () => {
    await refreshSelectedStrategy();
    if (embedded && strategyId) {
      dispatchWorkspacePineComposeRefresh(strategyId);
    }
  }, [embedded, refreshSelectedStrategy, strategyId]);

  useEffect(() => {
    if (!embedded || !strategyId) return;

    const onWorkspacePineRefresh = (event: Event) => {
      const detail = (event as CustomEvent<{ strategyId: string }>).detail;
      if (detail?.strategyId !== strategyId) return;
      void refreshSelectedStrategy();
    };

    window.addEventListener(WORKSPACE_PINE_COMPOSE_REFRESH, onWorkspacePineRefresh);
    return () => window.removeEventListener(WORKSPACE_PINE_COMPOSE_REFRESH, onWorkspacePineRefresh);
  }, [embedded, refreshSelectedStrategy, strategyId]);

  const revertPendingComposeSwitch = useCallback(() => {
    setPendingComposeSwitch((pending) => {
      if (pending) {
        const previousActive = pending.previousActiveComposedPolicyVersionIds[0] ?? null;
        setPreferredComposePolicyVersionId(
          previousActive ?? pending.previousPreferredComposePolicyVersionId
        );
      }
      return null;
    });
  }, []);

  const handleComposeCancel = useCallback(() => {
    revertPendingComposeSwitch();
    onComposeClose();
  }, [onComposeClose, revertPendingComposeSwitch]);

  const handleComposeSuccess = useCallback(async () => {
    setPendingComposeSwitch(null);
    await refreshSelectedStrategy();
    if (embedded && strategyId) {
      dispatchWorkspacePineComposeRefresh(strategyId);
    }
  }, [embedded, refreshSelectedStrategy, strategyId]);

  const openSetActivePineDialog = useCallback((version: ReportStrategyPolicyVersion) => {
    setSetActivePineTarget(version);
  }, []);

  const confirmSetActiveComposedPine = useCallback(async () => {
    if (!strategyId || !setActivePineTarget || !selectedStrategy) return;
    setSetActivePineBusy(true);
    try {
      setPendingComposeSwitch({
        previousPreferredComposePolicyVersionId: preferredComposePolicyVersionId,
        previousActiveComposedPolicyVersionIds: getActiveComposedPolicyVersionIds(
          selectedStrategy.active_pine,
          selectedStrategy
        ),
      });
      setPreferredComposePolicyVersionId(setActivePineTarget.id);
      setSetActivePineTarget(null);
      await refreshSelectedStrategy();
      requestActivePineDialog("compose");
    } catch (e) {
      presentMutationError(e, "Could not start recompose");
    } finally {
      setSetActivePineBusy(false);
    }
  }, [
    preferredComposePolicyVersionId,
    refreshSelectedStrategy,
    requestActivePineDialog,
    selectedStrategy,
    setActivePineTarget,
    strategyId,
  ]);

  const archiveCurrentStrategy = useCallback(async () => {
    if (!strategyId) return;
    setArchivingStrategyId(strategyId);
    try {
      const updated = await updateReportStrategy(strategyId, { is_archived: true });
      setSelectedStrategy(updated);
      toast.success("Strategy archived");
    } catch (e) {
      presentMutationError(e, "Archive failed");
    } finally {
      setArchivingStrategyId(null);
    }
  }, [strategyId]);

  const restoreCurrentStrategy = useCallback(async () => {
    if (!strategyId) return;
    setRestoringStrategyId(strategyId);
    try {
      const updated = await updateReportStrategy(strategyId, { is_archived: false });
      setSelectedStrategy(updated);
      toast.success("Strategy restored");
    } catch (e) {
      presentMutationError(e, "Restore failed");
    } finally {
      setRestoringStrategyId(null);
    }
  }, [strategyId]);

  const deleteCurrentStrategy = useCallback(async () => {
    if (!selectedStrategy) return;
    const label = selectedStrategy.name?.trim() || "this strategy";
    if (
      !window.confirm(
        `Delete "${label}" permanently? All policy versions will be removed. Existing reports will stay but will no longer be linked to this strategy. This cannot be undone.`
      )
    ) {
      return;
    }
    setDeletingStrategyId(strategyId);
    try {
      await deleteReportStrategy(strategyId);
      toast.success("Strategy deleted");
      if (embedded) {
        onDeleted?.();
      } else {
        history.replace(strategyListExitPath);
      }
    } catch (e) {
      presentMutationError(e, "Delete failed");
    } finally {
      setDeletingStrategyId(null);
    }
  }, [embedded, history, onDeleted, selectedStrategy, strategyId, strategyListExitPath]);





  const archivePolicyVersion = useCallback(
    async (v: ReportStrategyPolicyVersion, archive: boolean) => {
      if (!strategyId) return;
      try {
        await updateReportStrategyPolicyVersion(strategyId, v.id, { is_archived: archive });
        toast.success(archive ? "Version archived" : "Version restored");
        await loadVersions(strategyId);
      } catch (e) {
        presentMutationError(e, archive ? "Archive failed" : "Restore failed");
      }
    },
    [loadVersions, strategyId]
  );

  const composeEligibleVersions = useMemo(
    () => filterPolicyVersionsForCompose(versions, false),
    [versions]
  );

  const activePolicyVersions = useMemo(
    () => versions.filter((v) => !v.is_archived),
    [versions]
  );
  const archivedPolicyVersions = useMemo(
    () => versions.filter((v) => v.is_archived),
    [versions]
  );

  const policyVersionBacktestIds = useMemo(
    () => versions.map((version) => version.source_backtest_id),
    [versions]
  );
  const { reportIdByBacktestId, loading: reportLinksLoading } =
    usePolicyVersionBacktestReports(policyVersionBacktestIds);

  const onPolicyVersionViewChange = useCallback((value: string) => {
    if (value === "active" || value === "archived") {
      setPolicyVersionView(value);
    }
  }, []);

  const openBacktest = useCallback(
    (backtestId: string) => {
      if (!backtestId) return;
      history.push(simulatorResultsWithBacktestPath(backtestId));
    },
    [history]
  );

  const openOriginReportFromBacktest = useCallback(
    async (backtestId: string) => {
      if (!backtestId) return;
      try {
        const bt = await getBacktest(backtestId);
        const reportId = (bt as any)?.report_id;
        if (typeof reportId === "string" && reportId.trim()) {
          history.push(`/reports/${encodeURIComponent(reportId)}`);
          return;
        }
        toast.info("No report linked", {
          description: "This simulation does not include a report_id.",
        });
      } catch (e) {
        presentMutationError(e, "Could not open report");
      }
    },
    [history]
  );

  const activeComposedPolicyVersionIds = useMemo(
    () => getActiveComposedPolicyVersionIds(selectedStrategy?.active_pine, selectedStrategy),
    [selectedStrategy]
  );

  const executeDeletePolicyVersions = useCallback(
    async (policyVersionIds: string[]): Promise<boolean> => {
      if (!strategyId) return false;
      const uniqueIds = Array.from(new Set(policyVersionIds.filter(Boolean)));
      if (uniqueIds.length === 0) return false;

      const failures: string[] = [];
      for (const policyVersionId of uniqueIds) {
        try {
          await deleteReportStrategyPolicyVersion(strategyId, policyVersionId);
        } catch (e) {
          failures.push(
            e instanceof Error ? e.message : `Could not delete version ${policyVersionId}.`
          );
        }
      }

      if (failures.length === 0) {
        toast.info(
          uniqueIds.length === 1 ? "Deleted" : `Deleted ${uniqueIds.length} policy versions`
        );
      } else if (failures.length < uniqueIds.length) {
        toastWarning(`Deleted ${uniqueIds.length - failures.length} of ${uniqueIds.length}`, {
          description: failures[0],
        });
      } else {
        toastError("Delete failed", { description: failures[0] });
      }

      await loadDetail(strategyId);
      await loadVersions(strategyId);
      return failures.length < uniqueIds.length;
    },
    [loadDetail, loadVersions, strategyId]
  );

  const openDeletePolicyVersionsDialog = useCallback((policyVersionIds: string[]) => {
    const uniqueIds = Array.from(new Set(policyVersionIds.filter(Boolean)));
    if (uniqueIds.length === 0) return;
    setDeletePolicyVersionsRequest({ policyVersionIds: uniqueIds });
  }, []);

  const closeDeletePolicyVersionsDialog = useCallback(
    (open: boolean) => {
      if (!open && !policyBulkDeleteBusy) {
        setDeletePolicyVersionsRequest(null);
      }
    },
    [policyBulkDeleteBusy]
  );

  const handleConfirmDeletePolicyVersions = useCallback(async () => {
    if (!deletePolicyVersionsRequest || policyBulkDeleteBusy) return;

    const ids = Array.from(new Set(deletePolicyVersionsRequest.policyVersionIds.filter(Boolean)));
    if (ids.length === 0) return;

    setPolicyBulkDeleteBusy(true);
    try {
      const deleted = await executeDeletePolicyVersions(ids);
      if (deleted) {
        if (policyVersionView === "active") {
          setActivePolicyRowSelection({});
        } else {
          setArchivedPolicyRowSelection({});
        }
        setDeletePolicyVersionsRequest(null);
      }
    } finally {
      setPolicyBulkDeleteBusy(false);
    }
  }, [
    deletePolicyVersionsRequest,
    executeDeletePolicyVersions,
    policyBulkDeleteBusy,
    policyVersionView,
  ]);

  const selectedPolicyVersionIds = useMemo(() => {
    const selection =
      policyVersionView === "active" ? activePolicyRowSelection : archivedPolicyRowSelection;
    return Object.entries(selection)
      .filter(([, selected]) => selected)
      .map(([id]) => id);
  }, [activePolicyRowSelection, archivedPolicyRowSelection, policyVersionView]);

  const runBulkDeleteSelectedPolicies = useCallback(() => {
    if (selectedPolicyVersionIds.length === 0 || policyBulkDeleteBusy) return;
    openDeletePolicyVersionsDialog(selectedPolicyVersionIds);
  }, [
    openDeletePolicyVersionsDialog,
    policyBulkDeleteBusy,
    selectedPolicyVersionIds,
  ]);

  const openPineForVersion = useCallback(
    async (v: ReportStrategyPolicyVersion) => {
      if (!strategyId) return;
      setPineError(null);
      setPineText("");
      setPineVersionLabel(policyVersionDisplayTitle(v));
      setPineModalOpen(true);
      setPineLoading(true);
      try {
        const resp = await getReportStrategyPolicyVersionPineScript(strategyId, v.id, {
          policy_row_version: v.policy_row_version,
          label_style: "descriptive",
        });
        setPineText(typeof resp?.pinescript === "string" ? resp.pinescript : "");
      } catch (e) {
        setPineError(e instanceof Error ? e.message : "Failed to load Pinescript.");
      } finally {
        setPineLoading(false);
      }
    },
    [strategyId]
  );

  const copyPine = useCallback(async () => {
    if (!pineText) return;
    try {
      await navigator.clipboard.writeText(pineText);
      toast.success("Copied Pinescript");
    } catch (e) {
      presentMutationError(e, "Copy failed");
    }
  }, [pineText]);

  const showStrategyDetailLoading = detailLoading && !selectedStrategy && !detailError;
  const strategyActionsDisabled = !selectedStrategy || detailLoading;

  const [masterPineMounted, setMasterPineMounted] = useState(false);
  const [activePineMounted, setActivePineMounted] = useState(false);

  useEffect(() => {
    setMasterPineMounted(false);
    setActivePineMounted(false);
  }, [strategyId]);

  useEffect(() => {
    if (!embedded || !strategyId) return;
    const onVisibility = (event: Event) => {
      const detail = (event as CustomEvent<WorkspacePineDockVisibilityDetail>).detail;
      if (!detail || detail.strategyId !== strategyId) return;
      setMasterPineMounted(detail.masterVisible);
      setActivePineMounted(detail.activeVisible);
    };
    window.addEventListener(WORKSPACE_PINE_DOCK_VISIBILITY, onVisibility);
    return () => window.removeEventListener(WORKSPACE_PINE_DOCK_VISIBILITY, onVisibility);
  }, [embedded, strategyId]);

  const hasActivePineGenerated = strategyHasTradingViewPinescriptCreated(selectedStrategy);
  const canViewMasterPine =
    embedded &&
    Boolean(strategyId) &&
    strategyHasMasterPinescriptUploaded(selectedStrategy) &&
    !masterPineMounted;
  const canViewActivePine =
    embedded && Boolean(strategyId) && hasActivePineGenerated && !activePineMounted;

  const drawerActionItems = useMemo((): WorkspaceDrawerActionItem[] => {
    const items: WorkspaceDrawerActionItem[] = [];

    if (canViewActivePine) {
      items.push({
        id: "view-active-pine",
        label: "View Active Pinescript",
        disabled: strategyActionsDisabled,
        tone: "brand",
        icon: <CodeIcon className="size-4 shrink-0 text-current" aria-hidden />,
        onSelect: () => {
          if (strategyId) dispatchWorkspacePineActiveShow(strategyId);
        },
      });
    }

    if (canViewMasterPine) {
      items.push({
        id: "view-master-pine",
        label: "View Master Pinescript",
        disabled: strategyActionsDisabled,
        icon: <FileCode className="size-4 shrink-0 text-current" aria-hidden />,
        onSelect: () => {
          if (strategyId) dispatchWorkspacePineMasterShow(strategyId);
        },
      });
    }

    if (!embedded) {
      items.push({
        id: "edit-strategy",
        label: "Edit strategy",
        disabled: strategyActionsDisabled,
        icon: <Pencil className="size-4 shrink-0 text-current" aria-hidden />,
        onSelect: () => onEditOpen(),
      });
    }

    if (selectedStrategy?.is_archived) {
      items.push({
        id: "restore-strategy",
        label: restoringStrategyId === strategyId ? "Restoring…" : "Restore",
        disabled: !strategyId || detailLoading || restoringStrategyId === strategyId,
        icon: <RotateCcw className="size-4 shrink-0 text-current" aria-hidden />,
        onSelect: () => {
          void restoreCurrentStrategy();
        },
      });
    } else {
      items.push({
        id: "archive-strategy",
        label: archivingStrategyId === strategyId ? "Archiving…" : "Archive",
        disabled: !strategyId || detailLoading || archivingStrategyId === strategyId,
        tone: "orange",
        icon: <Archive className="size-4 shrink-0 text-current" aria-hidden />,
        onSelect: () => {
          void archiveCurrentStrategy();
        },
      });
    }

    items.push({
      id: "delete-strategy",
      label: deletingStrategyId === strategyId ? "Deleting…" : "Delete",
      disabled: !strategyId || detailLoading || deletingStrategyId === strategyId,
      tone: "destructive",
      separatorBefore: true,
      icon: <Trash2 className="size-4 shrink-0 text-current" aria-hidden />,
      onSelect: () => {
        void deleteCurrentStrategy();
      },
    });

    items.push({
      id: "need-help",
      label: "Need Help?",
      href: "https://help.tesseratrades.com/strategies",
      tone: "brand",
      separatorBefore: true,
      icon: <HelpCircle className="size-4 shrink-0 text-current" aria-hidden />,
    });

    return items;
  }, [
    archiveCurrentStrategy,
    archivingStrategyId,
    canViewActivePine,
    canViewMasterPine,
    deleteCurrentStrategy,
    deletingStrategyId,
    detailLoading,
    embedded,
    onEditOpen,
    restoreCurrentStrategy,
    restoringStrategyId,
    selectedStrategy?.is_archived,
    strategyActionsDisabled,
    strategyId,
  ]);

  const headerToolbar = useMemo(
    () => (
      <WorkspaceDrawerActionRow onClose={onClose}>
        <WorkspaceDrawerActionsMenu items={drawerActionItems} />
      </WorkspaceDrawerActionRow>
    ),
    [drawerActionItems, onClose]
  );

  useEffect(() => {
    if (embedded || !shadcnInset) {
      setInsetHeaderActions(null);
      return;
    }
    if (showStrategyDetailLoading || !selectedStrategy) {
      setInsetHeaderActions(null);
      return;
    }
    setInsetHeaderActions(headerToolbar);
    return () => setInsetHeaderActions(null);
  }, [
    embedded,
    headerToolbar,
    selectedStrategy,
    setInsetHeaderActions,
    shadcnInset,
    showStrategyDetailLoading,
  ]);

  const sectionCardProps = reportDetailSectionCardProps;

  const strategyDetailMetaItems = useMemo(() => {
    if (!selectedStrategy) return [];
    const activePolicyVersionCount = versions.filter((v) => !v.is_archived).length;
    const archivedPolicyVersionCount = versions.filter((v) => v.is_archived).length;
    const activeComposedVersions = versions.filter((v) =>
      activeComposedPolicyVersionIds.includes(v.id)
    );
    const currentVersionLabel =
      activeComposedVersions.length > 0
        ? activeComposedVersions.map((v) => versionLabel(v)).join(", ")
        : null;
    return buildStrategyDetailMetaItems({
      strategyId: selectedStrategy.id,
      policyVersionCount: versions.length,
      activePolicyVersionCount,
      archivedPolicyVersionCount,
      currentVersionLabel,
      strategy: selectedStrategy,
      createdAtDisplay: formatStrategyTimestamp(selectedStrategy.created_at, displayTimeZone),
      updatedAtDisplay: formatStrategyTimestamp(selectedStrategy.updated_at, displayTimeZone),
    });
  }, [activeComposedPolicyVersionIds, displayTimeZone, selectedStrategy, versions]);

  const strategyDetailPanels = selectedStrategy ? (
    <>
      <Card {...strategyDetailHeaderCardProps}>
        <CardHeader pb="0" w="100%" minW={0}>
          <Box data-reports-card-chrome="" w="100%" minW={0}>
            <Heading
              as="h2"
              noOfLines={3}
              title={selectedStrategy.name}
              fontSize={{ base: "2xl", md: "3xl" }}
              color="#38BBD1"
              fontWeight="normal"
              fontFamily="'Urbanist', sans-serif"
              lineHeight="1.15"
              mt={0}
              mb={0}
              minW={0}
              maxW="100%"
            >
              {selectedStrategy.name}
            </Heading>
            <StrategyCardMetaRowWithBadges
              metaItems={strategyDetailMetaItems}
              strategy={selectedStrategy}
            />
          </Box>
        </CardHeader>
        {!shadcnInset && !embedded ? (
          <CardBody pt={4} pb={4}>
            <FlexAny justify="flex-end" wrap="wrap" gap={2}>
              <HStack spacing={2} flexWrap="wrap">
                <Button
                  variant="outline"
                  onClick={() => onEditOpen()}
                  isDisabled={detailLoading}
                >
                  Edit strategy
                </Button>
                {selectedStrategy.is_archived ? (
                  <Button
                    variant="outline"
                    onClick={() => void restoreCurrentStrategy()}
                    isLoading={restoringStrategyId === strategyId}
                    loadingText="Restoring…"
                    isDisabled={!strategyId || detailLoading}
                  >
                    Restore
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    colorScheme="orange"
                    borderColor="#F49113"
                    color="#F49113"
                    _hover={{ borderColor: "#F49113", color: "#F49113", bg: "rgba(244, 145, 19, 0.08)" }}
                    onClick={() => void archiveCurrentStrategy()}
                    isLoading={archivingStrategyId === strategyId}
                    loadingText="Archiving…"
                    isDisabled={!strategyId || detailLoading}
                  >
                    Archive
                  </Button>
                )}
                <Button
                  variant="outline"
                  colorScheme="red"
                  onClick={() => void deleteCurrentStrategy()}
                  isLoading={deletingStrategyId === strategyId}
                  loadingText="Deleting…"
                  isDisabled={!strategyId || detailLoading}
                >
                  Delete permanently
                </Button>
              </HStack>
            </FlexAny>
          </CardBody>
        ) : null}
      </Card>

      <Tabs
        data-trade-log-tabs=""
        className="w-full min-w-0"
        value={detailTab}
        onValueChange={setDetailTab}
      >
        <div className="w-full overflow-x-auto overflow-y-hidden">
          <TabsList className="min-w-max">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="policies">Policy versions</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent
          value="overview"
          forceMount
          className="mt-0 min-w-0 data-[state=inactive]:hidden"
        >
          <Card {...sectionCardProps} data-strategy-overview="">
            <CardBody>
          <VStack align="stretch" spacing={4}>
            <StrategyDescriptionPanel description={selectedStrategy.description} />

            {!embedded ? (
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:items-start">
                <div className="min-w-0">
                  <StrategyActiveTradingViewPine
                    strategyId={selectedStrategy.id}
                    strategy={selectedStrategy}
                    currentPolicyVersionId={currentPolicyVersionId}
                    activePolicyVersionCount={versions.filter((v) => !v.is_archived).length}
                    subtleText={subtleText}
                    textColor={textColor}
                    borderColor={borderColor}
                    composeVersions={composeEligibleVersions}
                    preferredComposePolicyVersionId={preferredComposePolicyVersionId}
                    parseResult={editOpen ? editPineSession?.result ?? null : null}
                    onStrategyRefresh={refreshSelectedStrategyAndWorkspacePine}
                    onComposeSuccess={handleComposeSuccess}
                    autoOpenSignal={activePineAutoOpenSignal}
                    autoOpenMode={activePineAutoOpenMode}
                    onAutoOpenConsumed={handleActivePineAutoOpenConsumed}
                    onComposeCancel={revertPendingComposeSwitch}
                    onNavigateToPoliciesTab={navigateToPoliciesTab}
                    composeStartSignal={activePineComposeStartSignal}
                    composeFailedSignal={activePineComposeFailedSignal}
                    onComposeStarted={handleActivePineComposeStarted}
                  />
                </div>

                <div className="min-w-0">
                  <StrategyMasterPineSummary
                    strategyId={selectedStrategy.id}
                    strategy={selectedStrategy}
                    subtleText={subtleText}
                    textColor={textColor}
                    borderColor={borderColor}
                    onEditMaster={() => onEditOpen()}
                  />
                </div>
              </div>
            ) : null}
          </VStack>
            </CardBody>
          </Card>
        </TabsContent>

        <TabsContent value="policies" className="mt-0 min-w-0">
          <Card {...sectionCardProps} data-page-section="" w="100%" minW={0} overflow="hidden">
            <CardBody minW={0} overflow="hidden">
          <VStack align="stretch" spacing={4} minW={0} w="100%">
            <Box data-policy-versions-panel="" minW={0} w="100%">
              <Tabs
                value={policyVersionView}
                onValueChange={onPolicyVersionViewChange}
                className="w-full min-w-0"
              >
                <div
                  data-strategies-table-header=""
                  className="mb-2 flex w-full flex-wrap items-center justify-between gap-2"
                >
                  <TabsList className="min-w-max shrink-0">
                    <TabsTrigger value="active">Active</TabsTrigger>
                    <TabsTrigger value="archived">Archived</TabsTrigger>
                  </TabsList>
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    {selectedPolicyVersionIds.length > 0 ? (
                      <>
                        {policyVersionView === "active" && selectedPolicyVersionIds.length >= 2 ? (
                          <UiButton
                            type="button"
                            variant="outline-brand"
                            size="sm"
                            data-font="primary"
                            className="h-8 shrink-0 px-3"
                            onClick={onCombinePoliciesOpen}
                          >
                            <span className="normal-case tracking-normal">Combine policies</span>
                          </UiButton>
                        ) : null}
                        <UiButton
                          type="button"
                          variant="outline"
                          size="sm"
                          data-font="primary"
                          data-trade-log-action="delete"
                          className="h-8 shrink-0 !border-red-500 !text-red-500"
                          disabled={policyBulkDeleteBusy}
                          onClick={runBulkDeleteSelectedPolicies}
                        >
                          <span className="normal-case tracking-normal">
                            {policyBulkDeleteBusy ? "Deleting…" : "Delete selected"}
                          </span>
                        </UiButton>
                      </>
                    ) : null}
                  </div>
                </div>

                {versionsError && (
                  <Alert status="error" borderRadius="12px" mb={3}>
                    <AlertIcon />
                    <AlertDescription>{versionsError}</AlertDescription>
                  </Alert>
                )}

                {versionsLoading ? (
                  <Flex align="center" justify="center" py={8}>
                    <Spinner />
                  </Flex>
                ) : (
                  <>
                    <TabsContent value="active" className="mt-0 min-w-0">
                      <StrategyPolicyVersionsTable
                        versions={activePolicyVersions}
                        versionLabels={versionLabels}
                        activeComposedPolicyVersionIds={activeComposedPolicyVersionIds}
                        emptyMessage="No active policy versions yet."
                        onViewPolicy={(v) => {
                          setPolicyModalTitle(policyVersionDisplayTitle(v));
                          setPolicyModalTree(v.policy_tree);
                          setPolicyModalVersion(v);
                          setPolicyModalOpen(true);
                        }}
                        onViewPine={(v) => void openPineForVersion(v)}
                        onOpenBacktest={openBacktest}
                        onOpenReportFromBacktest={(backtestId) =>
                          void openOriginReportFromBacktest(backtestId)
                        }
                        reportIdByBacktestId={reportIdByBacktestId}
                        reportLinksLoading={reportLinksLoading}
                        onArchiveVersion={(v, archive) => void archivePolicyVersion(v, archive)}
                        onSetActiveComposedPine={openSetActivePineDialog}
                        onDeleteVersion={(id) => openDeletePolicyVersionsDialog([id])}
                        rowSelection={activePolicyRowSelection}
                        onRowSelectionChange={setActivePolicyRowSelection}
                      />
                    </TabsContent>
                    <TabsContent value="archived" className="mt-0 min-w-0">
                      <StrategyPolicyVersionsTable
                        versions={archivedPolicyVersions}
                        versionLabels={versionLabels}
                        activeComposedPolicyVersionIds={activeComposedPolicyVersionIds}
                        emptyMessage="No archived policy versions."
                        onViewPolicy={(v) => {
                          setPolicyModalTitle(policyVersionDisplayTitle(v));
                          setPolicyModalTree(v.policy_tree);
                          setPolicyModalVersion(v);
                          setPolicyModalOpen(true);
                        }}
                        onViewPine={(v) => void openPineForVersion(v)}
                        onOpenBacktest={openBacktest}
                        onOpenReportFromBacktest={(backtestId) =>
                          void openOriginReportFromBacktest(backtestId)
                        }
                        reportIdByBacktestId={reportIdByBacktestId}
                        reportLinksLoading={reportLinksLoading}
                        onArchiveVersion={(v, archive) => void archivePolicyVersion(v, archive)}
                        onSetActiveComposedPine={openSetActivePineDialog}
                        onDeleteVersion={(id) => openDeletePolicyVersionsDialog([id])}
                        rowSelection={archivedPolicyRowSelection}
                        onRowSelectionChange={setArchivedPolicyRowSelection}
                      />
                    </TabsContent>
                  </>
                )}
              </Tabs>
            </Box>
          </VStack>
            </CardBody>
          </Card>
        </TabsContent>
      </Tabs>
    </>
  ) : null;

  return (
    <FlexAny
      direction="column"
      pt={embedded || shadcnInset ? 0 : { base: "100px", md: "80px", lg: "40px" }}
      pb={embedded ? 0 : "24px"}
      px={embedded || shadcnInset ? 0 : { base: "20px", xl: "40px" }}
      gap={embedded ? "16px" : "24px"}
      w="100%"
      minW={0}
      flex={embedded ? 1 : undefined}
      minH={embedded ? "100%" : pageMinHeight}
      h={embedded ? "100%" : undefined}
      data-strategy-detail-layout={layout}
    >
      <div
        className={
          embedded
            ? "flex min-h-full w-full min-w-0 flex-1 flex-col gap-4"
            : "flex w-full min-w-0 flex-col gap-4"
        }
        data-strategy-detail-page=""
      >
        {!embedded && !shadcnInset && selectedStrategy && !showStrategyDetailLoading ? (
          headerToolbar
        ) : null}
        {embedded && selectedStrategy && !showStrategyDetailLoading ? headerToolbar : null}

        {showStrategyDetailLoading ? (
          <div
            className={
              embedded
                ? "flex min-h-full flex-1 flex-col items-center justify-center"
                : undefined
            }
          >
            <StatisticsEmptyState
              variant="loading"
              fillParent={embedded}
              title="Loading strategy"
              description="Fetching strategy details and policy versions."
            />
          </div>
        ) : detailError && !selectedStrategy && strategyId ? (
          <Alert status="error" borderRadius="12px">
            <AlertIcon />
            <AlertDescription>{detailError}</AlertDescription>
          </Alert>
        ) : embedded ? (
          <div
            data-strategy-detail-embedded=""
            className="flex w-full min-w-0 flex-col gap-4"
          >
            {strategyDetailPanels}
          </div>
        ) : (
          <div data-strategy-detail-main-card="" className={strategiesMainCardClassName}>
            {strategyDetailPanels}
          </div>
        )}
      </div>

      <EditStrategyDialog
        open={editOpen}
        onOpenChange={handleEditOpenChange}
        strategyId={strategyId}
        listRow={selectedStrategy}
        onParseSessionChange={setEditPineSession}
        onSaved={async (updated) => {
          setSelectedStrategy(updated);
          if (strategyId) await loadDetail(strategyId);
        }}
      />

      <StrategyPineScriptViewDialog
        open={pineModalOpen}
        onOpenChange={setPineModalOpen}
        title={pineVersionLabel}
        loading={pineLoading}
        error={pineError}
        pineText={pineText}
        onCopy={() => void copyPine()}
      />

      {selectedStrategy ? (
        <StrategyComposeModal
          shell="pine-detail"
          isOpen={composeOpen}
          onClose={handleComposeCancel}
          onCancel={handleComposeCancel}
          strategy={selectedStrategy}
          versions={composeEligibleVersions}
          currentPolicyVersionId={currentPolicyVersionId}
          preferredPolicyVersionId={preferredComposePolicyVersionId}
          subtleText={subtleText}
          textColor={textColor}
          borderColor={borderColor}
          onStrategyRefresh={refreshSelectedStrategyAndWorkspacePine}
          parseResult={editOpen ? editPineSession?.result ?? null : null}
          onComposeStart={notifyActivePineComposeStart}
          onComposeFailed={notifyActivePineComposeFailed}
          onComposeSuccess={handleComposeSuccess}
          onNavigateToPoliciesTab={() => {
            navigateToPoliciesTab();
            handleComposeCancel();
          }}
        />
      ) : null}

      {selectedStrategy && strategyId ? (
        <StrategySetActivePineDialog
          open={Boolean(setActivePineTarget)}
          onOpenChange={(open) => {
            if (!open && !setActivePineBusy) setSetActivePineTarget(null);
          }}
          policyVersionLabel={
            setActivePineTarget ? policyVersionDisplayTitle(setActivePineTarget) : "this policy version"
          }
          hasActiveComposedPine={strategyHasTradingViewPinescriptCreated(selectedStrategy)}
          busy={setActivePineBusy}
          onConfirm={confirmSetActiveComposedPine}
        />
      ) : null}

      {selectedStrategy && strategyId ? (
        <StrategyCombinePoliciesDialog
          open={combinePoliciesOpen}
          onOpenChange={(open) => (open ? onCombinePoliciesOpen() : onCombinePoliciesClose())}
          strategyId={strategyId}
          versions={composeEligibleVersions}
          selectedVersionIds={
            policyVersionView === "active" ? selectedPolicyVersionIds : []
          }
          onCombined={(id) => {
            setPreferredComposePolicyVersionId(id);
            setActivePolicyRowSelection({});
          }}
          onVersionsRefresh={async () => {
            await loadVersions(strategyId);
            await refreshSelectedStrategy();
          }}
        />
      ) : null}

      <StrategyPolicyViewDialog
        open={policyModalOpen}
        onOpenChange={setPolicyModalOpen}
        title={policyModalTitle}
        policyTree={policyModalTree}
        policyVersions={versions}
        policyVersion={policyModalVersion}
        strategyId={strategyId}
        onTitleSaved={async (updated) => {
          setPolicyModalVersion(updated);
          setPolicyModalTitle(policyVersionDisplayTitle(updated));
          await loadVersions(strategyId);
          await refreshSelectedStrategy();
        }}
      />

      <DeletePolicyVersionsDialog
        open={deletePolicyVersionsRequest != null}
        onOpenChange={closeDeletePolicyVersionsDialog}
        request={deletePolicyVersionsRequest}
        activeComposedPolicyVersionIds={activeComposedPolicyVersionIds}
        isDeleting={policyBulkDeleteBusy}
        onConfirm={() => void handleConfirmDeletePolicyVersions()}
      />
    </FlexAny>
  );
}

